import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSession } from "@/lib/auth";
import { leaveChallenge, watchChallenge, type Challenge } from "@/data/challenges";
import { watchMembers, type Member } from "@/data/members";
import { silenciarGrupo, watchUser } from "@/data/users";
import { Avatar, Empty, Spinner } from "@/ui/primitives";
import { StoragePhoto } from "@/ui/StoragePhoto";
import {
  IconBack, IconBolt, IconChevron, IconPencil, IconPin, IconSair, IconTarget,
} from "@/ui/icons";
import { TabBar } from "@/ui/TabBar";

const dataCurta = (dia: string) => {
  const [a, m, d] = dia.split("-");
  return a && m && d ? `${d}/${m}/${a}` : dia;
};

export function Details() {
  const { cid = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useSession();
  const [challenge, setChallenge] = useState<Challenge | null | undefined>(undefined);
  const [members, setMembers] = useState<Member[]>([]);

  useEffect(() => watchChallenge(cid, setChallenge), [cid]);
  useEffect(() => watchMembers(cid, setMembers), [cid]);
  const [silenciado, setSilenciado] = useState(false);
  useEffect(() => (user ? watchUser(user.uid, (p) => setSilenciado(!!p?.silenciados?.includes(cid))) : undefined), [user, cid]);

  if (challenge === undefined) return <div className="screen center"><Spinner /></div>;
  if (!challenge) {
    return (
      <main className="screen">
        <Empty emoji="🤔" title="Desafio não encontrado" />
      </main>
    );
  }

  const dono = members.find((m) => m.uid === challenge.ownerUid);
  const souDono = challenge.ownerUid === user?.uid;
  const n = members.length;

  async function sair() {
    if (!user) return;
    if (!confirm("Sair do desafio? Seus pratos continuam lá.")) return;
    await leaveChallenge(cid, user.uid);
    navigate("/", { replace: true });
  }

  return (
    <main className="screen">
      <div className="topbar">
        <Link className="topbar-btn" to={`/c/${cid}`} aria-label="Voltar"><IconBack /></Link>
        <div className="topbar-title" />
        <span className="topbar-btn" />
      </div>

      <div className="screen-body">
        {/* A capa mora aqui agora. No feed ela empurrava o primeiro prato
            para 640px de rolagem todo dia para mostrar a mesma imagem;
            nesta tela ela é o que a pessoa veio ver — o que o desafio é. */}
        {challenge.coverPath
          ? <div className="banner"><StoragePhoto path={challenge.coverPath} alt="" ratio="16 / 9" /></div>
          : <div className="banner sem-capa">{challenge.name}</div>}

        <div className="page-head">
          <h1>{challenge.name}</h1>
          <p className="sub">Desde {dataCurta(challenge.startDay)}</p>
        </div>

        {dono && (
          <div className="dono-linha">
            <Avatar name={dono.name} photo={dono.photo} size="hero" />
            <div>
              <div className="dono-nome">{dono.name}</div>
              <div className="dono-papel">Dono do desafio</div>
            </div>
          </div>
        )}

        {challenge.description && <p className="descricao">{challenge.description}</p>}

        <div className="participantes-cabecalho">
          <div className="participantes-num">{n} {n === 1 ? "participante" : "participantes"}</div>
        </div>
        <div className="avatar-row">
          {members.map((m) => (
            <Link key={m.uid} to={`/c/${cid}/u/${m.uid}`} aria-label={m.name}>
              <Avatar name={m.name} photo={m.photo} size="lg" />
            </Link>
          ))}
        </div>

        <div className="card list-card">
          <Link className="list-row" to={`/c/${cid}/recap`}>
            <span className="ico"><IconBolt size={22} /></span>
            <span className="label">Recap do período</span>
            <span className="chev"><IconChevron size={18} /></span>
          </Link>
          {/* Nem o bate-papo nem o mapa têm linha aqui, e é a mesma razão:
              o mapa virou aba e o bate-papo virou ícone no topo do desafio.
              Repetir na lista o que a barra de baixo e a barra de cima já
              mostram faz esta lista parecer índice de tudo — e aí a pessoa
              passa a procurar tudo nela, inclusive o que nunca esteve. */}
          <Link className="list-row" to={`/c/${cid}/missoes`}>
            <span className="ico emoji">🎯</span>
            <span className="label">Missões</span>
            <span className="chev"><IconChevron size={18} /></span>
          </Link>
          <Link className="list-row" to={`/c/${cid}/classificacao`}>
            <span className="ico"><IconTarget size={22} /></span>
            <span className="label">Classificação</span>
            <span className="chev"><IconChevron size={18} /></span>
          </Link>
          <Link className="list-row" to={`/c/${cid}/convite`}>
            <span className="ico"><IconPin size={22} /></span>
            <span className="label">Convidar</span>
            <span className="value">{challenge.code}</span>
          </Link>
          {souDono && (
            <Link className="list-row" to={`/c/${cid}/editar`}>
              <span className="ico"><IconPencil size={22} /></span>
              <span className="label">
                Editar desafio
                <span className="pref-hint">nome, datas, pontos, refeições e selos</span>
              </span>
              <span className="chev"><IconChevron size={18} /></span>
            </Link>
          )}
          {/* Por grupo: quem está em vários escolhe de quais recebe. O
              servidor confere antes de cada envio (`push.ts`). */}
          <div className="switch-row">
            <div className="switch-corpo">
              <div className="switch-titulo">Silenciar este grupo</div>
              <div className="switch-sub">Nenhum aviso deste desafio. Os lembretes de refeição continuam.</div>
            </div>
            <button className={`switch ${silenciado ? "on" : ""}`} role="switch" aria-checked={silenciado}
              aria-label="Silenciar este grupo"
              onClick={() => {
                if (!user) return;
                setSilenciado(!silenciado);
                void silenciarGrupo(user.uid, cid, !silenciado).catch(() => setSilenciado(silenciado));
              }}>
              <span />
            </button>
          </div>
          <button className="list-row danger" onClick={sair}>
            {/* Ícone de TRAÇO, como o de todas as linhas acima. Aqui era
                um emoji de porta — colorido no meio de ícones monocromáticos,
                ele era a única coisa da lista com cara de adesivo. Sem ícone
                nenhum o "Deixar" ficaria recuado em relação às outras. */}
            <span className="ico"><IconSair size={22} /></span>
            <span className="label">{souDono ? "Sair (você é o dono)" : "Deixar"}</span>
          </button>
        </div>

        <div className="gap" />
      </div>

      <TabBar cid={cid} />
    </main>
  );
}
